import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import clubService from "../../services/clubService";
import { useNotification } from "../../context/NotificationContext";
import campusService from "../../services/campusService";
import universityService from "../../services/universityService";
import categoryService from "../../services/categoryService";

const ClubForm = ({ club, onSubmit, onCancel, loading }) => {
  const { t } = useTranslation("clubs");
  const { t: tCommon } = useTranslation("common");

  const [formData, setFormData] = useState({
    nameEn: "",
    nameLocal: "",
    descriptionEn: "",
    descriptionLocal: "",
    campusId: "",
    campusNameEn: "",
    campusNameLocal: "",
    universityId: "",
    universityNameEn: "",
    universityNameLocal: "",
    imageId: "",
    categoryIds: [],

    communication: {
      email: "",
      phone_number: "",
      instagram: "",
      twitter: "",
      whatsapp_phone_number: "",
    },
  });

  const [universities, setUniversities] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [universityLoading, setUniversityLoading] = useState(false);
  const [campusLoading, setCampusLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // University search states
  const [universitySearchTerm, setUniversitySearchTerm] = useState("");
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);
  const [universitySearchTimeout, setUniversitySearchTimeout] = useState(null);

  // Campus search states
  const [campusSearchTerm, setCampusSearchTerm] = useState("");
  const [filteredCampuses, setFilteredCampuses] = useState([]);
  const [showCampusDropdown, setShowCampusDropdown] = useState(false);
  const [campusSearchTimeout, setCampusSearchTimeout] = useState(null);

  const { showSuccess, showError } = useNotification();

  // Load form data when club prop changes
  useEffect(() => {
    if (club && categories.length > 0) {
      console.log("Loading club data in form:", club);
      console.log("Club communication data:", club.communication);
      setFormData({
        nameEn: club.nameEn || "",
        nameLocal: club.nameLocal || "",
        descriptionEn: club.descriptionEn || "",
        descriptionLocal: club.descriptionLocal || "",
        campusId: club.campusId || "",
        universityId: club.universityId || "",
        imageId: club.imageId || "",
        categoryIds:
          club.categories?.map((cat) => cat.id) || club.categoryIds || [],
        communication: {
          email: club.communication?.email || "",
          phone_number: club.communication?.phone_number || "",
          instagram: club.communication?.instagram || "",
          twitter: club.communication?.twitter || "",
          whatsapp_phone_number:
            club.communication?.whatsapp_phone_number || "",
        },
      });

      if (club.universityNameEn) {
        setUniversitySearchTerm(club.universityNameEn);
      }

      if (club.campusNameEn) {
        setCampusSearchTerm(club.campusNameEn);
      }

      if (club.imageUrl) {
        setImagePreview(club.imageUrl);
      }

      if (club.universityId) {
        loadCampusesByUniversity(club.universityId);
      }
    }
  }, [club, categories]);

  // Load universities, campuses and categories on component mount
  useEffect(() => {
    loadUniversities();
    loadCategories();
  }, []);

  // Load campuses when university changes
  useEffect(() => {
    if (formData.universityId) {
      loadCampusesByUniversity(formData.universityId);
    } else {
      setCampuses([]);
      setFilteredCampuses([]);
      setFormData((prev) => ({ ...prev, campusId: "" }));
      setCampusSearchTerm("");
    }
  }, [formData.universityId]);

  // University search effect
  useEffect(() => {
    if (universitySearchTimeout) {
      clearTimeout(universitySearchTimeout);
    }

    const timeout = setTimeout(() => {
      filterUniversities();
    }, 300);

    setUniversitySearchTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universitySearchTerm, universities]);

  // Campus search effect
  useEffect(() => {
    if (campusSearchTimeout) {
      clearTimeout(campusSearchTimeout);
    }

    const timeout = setTimeout(() => {
      filterCampuses();
    }, 300);

    setCampusSearchTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusSearchTerm, campuses]);

  const loadUniversities = async () => {
    try {
      setUniversityLoading(true);
      const response = await universityService.getUniversities(0, 1000);
      if (response.success) {
        const sortedUniversities = (response.data.data || []).sort((a, b) =>
          (a.nameLocal || a.nameEn || "").localeCompare(
            b.nameLocal || b.nameEn || "",
          ),
        );
        setUniversities(sortedUniversities);
        setFilteredUniversities(sortedUniversities);
      }
    } catch (error) {
      console.error("Error loading universities:", error);
    } finally {
      setUniversityLoading(false);
    }
  };

  const filterUniversities = () => {
    if (!universitySearchTerm.trim()) {
      setFilteredUniversities(universities);
      return;
    }

    const filtered = universities.filter(
      (university) =>
        (university.nameEn || "")
          .toLowerCase()
          .includes(universitySearchTerm.toLowerCase()) ||
        (university.nameLocal || "")
          .toLowerCase()
          .includes(universitySearchTerm.toLowerCase()),
    );
    setFilteredUniversities(filtered);
  };

  const filterCampuses = () => {
    if (!campusSearchTerm.trim()) {
      setFilteredCampuses(campuses);
      return;
    }

    const filtered = campuses.filter(
      (campus) =>
        (campus.nameEn || "")
          .toLowerCase()
          .includes(campusSearchTerm.toLowerCase()) ||
        (campus.nameLocal || "")
          .toLowerCase()
          .includes(campusSearchTerm.toLowerCase()),
    );
    setFilteredCampuses(filtered);
  };

  const handleUniversitySelect = (university) => {
    setFormData((prev) => ({
      ...prev,
      universityId: university.id,
      universityNameEn: university.nameEn,
      universityNameLocal: university.nameLocal,
      campusId: "",
    }));
    setUniversitySearchTerm(university.nameEn);
    setShowUniversityDropdown(false);
    setCampusSearchTerm("");
  };

  const handleUniversitySearchChange = (e) => {
    const value = e.target.value;
    setUniversitySearchTerm(value);
    setShowUniversityDropdown(true);

    if (!value.trim()) {
      setFilteredUniversities(universities);
    }
  };

  const handleUniversitySearchFocus = () => {
    setShowUniversityDropdown(true);
  };

  const handleUniversitySearchBlur = () => {
    setTimeout(() => setShowUniversityDropdown(false), 200);
  };

  const handleCampusSelect = (campus) => {
    setFormData((prev) => ({
      ...prev,
      campusId: campus.id,
      campusNameEn: campus.nameEn,
      campusNameLocal: campus.nameLocal,
    }));
    setCampusSearchTerm(campus.nameEn);
    setShowCampusDropdown(false);
  };

  const handleCampusSearchChange = (e) => {
    const value = e.target.value;
    setCampusSearchTerm(value);
    setShowCampusDropdown(true);

    if (!value.trim()) {
      setFilteredCampuses(campuses);
    }
  };

  const handleCampusSearchFocus = () => {
    setShowCampusDropdown(true);
  };

  const handleCampusSearchBlur = () => {
    setTimeout(() => setShowCampusDropdown(false), 200);
  };

  const loadCampusesByUniversity = async (universityId) => {
    try {
      setCampusLoading(true);
      const response = await campusService.getCampuses(
        0,
        1000,
        "",
        universityId,
      );
      if (response.success) {
        const sortedCampuses = (response.data.data || []).sort((a, b) =>
          (a.nameLocal || a.nameEn || "").localeCompare(
            b.nameLocal || b.nameEn || "",
          ),
        );
        setCampuses(sortedCampuses);
        setFilteredCampuses(sortedCampuses);
        setCampusSearchTerm("");
        setFormData((prev) => ({ ...prev, campusId: "" }));
      }
    } catch (error) {
      console.error("Error loading campuses:", error);
    } finally {
      setCampusLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await categoryService.getAllCategories();
      if (response.success) {
        setCategories(response.data.content || []);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("communication.")) {
      const communicationField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        communication: {
          ...prev.communication,
          [communicationField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCategoryChange = (categoryId) => {
    setFormData((prev) => {
      const categoryIds = prev.categoryIds.includes(categoryId)
        ? prev.categoryIds.filter((id) => id !== categoryId)
        : [...prev.categoryIds, categoryId];

      return {
        ...prev,
        categoryIds,
      };
    });

    if (errors.categoryIds) {
      setErrors((prev) => ({
        ...prev,
        categoryIds: "",
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showError(t("validation.invalidImageType"));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        showError(t("validation.imageTooLarge"));
        return;
      }

      setSelectedFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!selectedFile) return null;

    try {
      setUploadingImage(true);
      const response = await clubService.uploadPhoto(selectedFile);

      if (response.success) {
        showSuccess(t("validation.imageUploadSuccess"));
        return response.data.imageId;
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showError(t("validation.imageUploadFailed"));
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nameEn?.trim()) {
      newErrors.nameEn = t("validation.nameEnRequired");
    }

    if (!formData.nameLocal?.trim()) {
      newErrors.nameLocal = t("validation.nameLocalRequired");
    }

    if (!formData.descriptionEn?.trim()) {
      newErrors.descriptionEn = t("validation.descriptionEnRequired");
    }

    if (!formData.descriptionLocal?.trim()) {
      newErrors.descriptionLocal = t("validation.descriptionLocalRequired");
    }

    if (!formData.universityId) {
      newErrors.universityId = t("validation.universityRequired");
    }

    if (!formData.campusId) {
      newErrors.campusId = t("validation.campusRequired");
    } else if (!formData.universityId) {
      newErrors.campusId = t("validation.selectUniversityFirst");
    }

    if (formData.categoryIds.length === 0) {
      newErrors.categoryIds = t("validation.categoryRequired");
    }

    if (
      formData.communication.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.communication.email)
    ) {
      newErrors["communication.email"] = t("validation.invalidEmail");
    }

    if (
      formData.communication.phone_number &&
      !/^\+?[\d\s\-()+]+$/.test(formData.communication.phone_number)
    ) {
      newErrors["communication.phone_number"] = t("validation.invalidPhone");
    }

    if (
      formData.communication.whatsapp_phone_number &&
      !/^\+?[\d\s\-()+]+$/.test(formData.communication.whatsapp_phone_number)
    ) {
      newErrors["communication.whatsapp_phone_number"] = t(
        "validation.invalidWhatsapp",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        let imageId = formData.imageId;
        if (selectedFile && !imageId) {
          const uploadedImageId = await uploadImage();
          if (uploadedImageId) {
            imageId = uploadedImageId;
          } else {
            return;
          }
        }

        const submitData = {
          nameEn: formData.nameEn.trim(),
          nameLocal: formData.nameLocal.trim(),
          descriptionEn: formData.descriptionEn.trim(),
          descriptionLocal: formData.descriptionLocal.trim(),
          campusId: formData.campusId,

          imageId: imageId || null,
          categoryIds: formData.categoryIds.map((id) => parseInt(id)),
          communication: {
            email: formData.communication.email.trim() || null,
            phone_number: formData.communication.phone_number.trim() || null,
            instagram: formData.communication.instagram.trim() || null,
            twitter: formData.communication.twitter.trim() || null,
            whatsapp_phone_number:
              formData.communication.whatsapp_phone_number.trim() || null,
          },
        };

        console.log("Submitting club data:", submitData);
        console.log("Communication data being sent:", submitData.communication);
        console.log("Form data before submission:", formData.communication);

        onSubmit(submitData);
      } catch (error) {
        console.error("Error in form submission:", error);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="club-form">
      <div className="row g-4">
        {/* Basic Information Section */}
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-info-circle"></i>
              <h6>{t("form.basicInformation")}</h6>
            </div>

            <div className="row g-3">
              {/* Club Names */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("form.clubNameEn")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="nameEn"
                  className={`form-control ${
                    errors.nameEn ? "is-invalid" : ""
                  }`}
                  placeholder={t("form.nameEnPlaceholder")}
                  value={formData.nameEn}
                  onChange={handleInputChange}
                  disabled={loading || uploadingImage}
                />
                {errors.nameEn && (
                  <div className="invalid-feedback">{errors.nameEn}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("form.clubNameLocal")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="nameLocal"
                  className={`form-control ${
                    errors.nameLocal ? "is-invalid" : ""
                  }`}
                  placeholder={t("form.nameLocalPlaceholder")}
                  value={formData.nameLocal}
                  onChange={handleInputChange}
                  disabled={loading || uploadingImage}
                />
                {errors.nameLocal && (
                  <div className="invalid-feedback">{errors.nameLocal}</div>
                )}
              </div>

              {/* Descriptions */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("form.descriptionEn")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <textarea
                  name="descriptionEn"
                  className={`form-control ${
                    errors.descriptionEn ? "is-invalid" : ""
                  }`}
                  rows="4"
                  placeholder={t("form.descriptionEnPlaceholder")}
                  value={formData.descriptionEn}
                  onChange={handleInputChange}
                  disabled={loading || uploadingImage}
                />
                {errors.descriptionEn && (
                  <div className="invalid-feedback">{errors.descriptionEn}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("form.descriptionLocal")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <textarea
                  name="descriptionLocal"
                  className={`form-control ${
                    errors.descriptionLocal ? "is-invalid" : ""
                  }`}
                  rows="4"
                  placeholder={t("form.descriptionLocalPlaceholder")}
                  value={formData.descriptionLocal}
                  onChange={handleInputChange}
                  disabled={loading || uploadingImage}
                />
                {errors.descriptionLocal && (
                  <div className="invalid-feedback">
                    {errors.descriptionLocal}
                  </div>
                )}
              </div>

              {/* University Selection */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("fields.university")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <div className="position-relative">
                  <div className="input-group">
                    <input
                      type="text"
                      name="universityNameEn"
                      className={`form-control ${
                        errors.universityId ? "is-invalid" : ""
                      }`}
                      placeholder={t("searchUniversities")}
                      value={universitySearchTerm}
                      onChange={handleUniversitySearchChange}
                      onFocus={handleUniversitySearchFocus}
                      onBlur={handleUniversitySearchBlur}
                      disabled={loading || universityLoading || uploadingImage}
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() =>
                        setShowUniversityDropdown(!showUniversityDropdown)
                      }
                      disabled={loading || universityLoading || uploadingImage}
                    >
                      <i className="fas fa-search"></i>
                    </button>
                  </div>

                  <input
                    type="hidden"
                    name="universityId"
                    value={formData.universityId}
                  />

                  {errors.universityId && (
                    <div className="invalid-feedback d-block">
                      {errors.universityId}
                    </div>
                  )}
                  {universityLoading && (
                    <small className="text-muted">
                      {t("loadingUniversities")}
                    </small>
                  )}

                  {showUniversityDropdown &&
                    filteredUniversities.length > 0 && (
                      <div className="university-dropdown">
                        {filteredUniversities.map((university) => (
                          <button
                            key={university.id}
                            type="button"
                            className="university-dropdown-item"
                            onClick={() => handleUniversitySelect(university)}
                          >
                            <div className="university-name-en">
                              {university.nameEn}
                            </div>
                            <div className="university-name-local">
                              {university.nameLocal}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                  {showUniversityDropdown &&
                    filteredUniversities.length === 0 && (
                      <div className="university-dropdown">
                        <div className="university-dropdown-item text-muted">
                          {t("noUniversitiesFound")}
                        </div>
                      </div>
                    )}
                </div>
              </div>

              {/* Campus Selection */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("fields.campus")} <span className="text-danger">*</span>
                </label>
                <div className="position-relative">
                  <div className="input-group">
                    <input
                      type="text"
                      name="campusNameEn"
                      className={`form-control ${
                        errors.campusId ? "is-invalid" : ""
                      }`}
                      placeholder={t("searchCampuses")}
                      value={campusSearchTerm}
                      onChange={handleCampusSearchChange}
                      onFocus={handleCampusSearchFocus}
                      onBlur={handleCampusSearchBlur}
                      disabled={
                        loading ||
                        campusLoading ||
                        uploadingImage ||
                        !formData.universityId
                      }
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setShowCampusDropdown(!showCampusDropdown)}
                      disabled={
                        loading ||
                        campusLoading ||
                        uploadingImage ||
                        !formData.universityId
                      }
                    >
                      <i className="fas fa-search"></i>
                    </button>
                  </div>

                  <input
                    type="hidden"
                    name="campusId"
                    value={formData.campusId}
                  />

                  {errors.campusId && (
                    <div className="invalid-feedback d-block">
                      {errors.campusId}
                    </div>
                  )}
                  {campusLoading && (
                    <small className="text-muted">{t("loadingCampuses")}</small>
                  )}
                  {!formData.universityId && (
                    <small className="text-muted">
                      {t("validation.selectUniversityFirst")}
                    </small>
                  )}

                  {showCampusDropdown && filteredCampuses.length > 0 && (
                    <div className="university-dropdown">
                      {filteredCampuses.map((campus) => (
                        <button
                          key={campus.id}
                          type="button"
                          className="university-dropdown-item"
                          onClick={() => handleCampusSelect(campus)}
                        >
                          <div className="university-name-en">
                            {campus.nameEn}
                          </div>
                          <div className="university-name-local">
                            {campus.nameLocal}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {showCampusDropdown && filteredCampuses.length === 0 && (
                    <div className="university-dropdown">
                      <div className="university-dropdown-item text-muted">
                        {t("noCampusesFound")}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Categories Selection */}
              <div className="col">
                <label className="form-label fw-bold">
                  {t("categories")} <span className="text-danger">*</span>
                </label>
                <div className="categories-container">
                  {categoriesLoading ? (
                    <div className="text-muted">
                      <i className="fas fa-spinner fa-spin me-2"></i>
                      {tCommon("actions.loading")}
                    </div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {categories.map((category) => (
                        <div
                          key={category.id}
                          className={`category-badge ${
                            formData.categoryIds.includes(category.id)
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => handleCategoryChange(category.id)}
                          style={{
                            cursor: "pointer",
                            padding: "0.5rem 1rem",
                            borderRadius: "0.5rem",
                            backgroundColor: formData.categoryIds.includes(
                              category.id,
                            )
                              ? "#0d6efd"
                              : "#f8f9fa",
                            color: formData.categoryIds.includes(category.id)
                              ? "white"
                              : "#495057",
                            border: "1px solid #dee2e6",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <i className="fas fa-tag me-1"></i>
                          {category.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {errors.categoryIds && (
                  <div className="text-danger small mt-1">
                    {errors.categoryIds}
                  </div>
                )}
                <div className="text-muted small mt-2">
                  {t("form.selectedCategories", {
                    count: formData.categoryIds.length,
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Image Upload Section */}
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-image"></i>
              <h6>{t("form.clubImage")}</h6>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("form.uploadClubImage")}
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={loading || uploadingImage}
                />
                <small className="text-muted">{t("form.imageFormats")}</small>
                {uploadingImage && (
                  <div className="mt-2">
                    <div className="d-flex align-items-center">
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></div>
                      <small className="text-primary">
                        {t("form.uploadingImage")}
                      </small>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-md-6">
                {imagePreview && (
                  <div>
                    <label className="form-label fw-bold">
                      {t("form.imagePreview")}
                    </label>
                    <div className="club-logo-preview">
                      <img src={imagePreview} alt={t("form.imagePreview")} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Communication Section */}
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-envelope"></i>
              <h6>{t("form.communicationContact")}</h6>
            </div>

            <div className="row g-3">
              {/* Email */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("fields.email")}
                </label>
                <input
                  type="email"
                  name="communication.email"
                  className={`form-control ${
                    errors["communication.email"] ? "is-invalid" : ""
                  }`}
                  placeholder="contact@club.com"
                  value={formData.communication.email}
                  onChange={handleInputChange}
                  disabled={loading || uploadingImage}
                />
                {errors["communication.email"] && (
                  <div className="invalid-feedback">
                    {errors["communication.email"]}
                  </div>
                )}
              </div>

              {/* Phone */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("form.phoneNumber")}
                </label>
                <input
                  type="tel"
                  name="communication.phone_number"
                  className={`form-control ${
                    errors["communication.phone_number"] ? "is-invalid" : ""
                  }`}
                  placeholder="+1 (555) 123-4567"
                  value={formData.communication.phone_number}
                  onChange={handleInputChange}
                  disabled={loading || uploadingImage}
                />
                {errors["communication.phone_number"] && (
                  <div className="invalid-feedback">
                    {errors["communication.phone_number"]}
                  </div>
                )}
              </div>

              {/* WhatsApp */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("form.whatsappNumber")}
                </label>
                <input
                  type="tel"
                  name="communication.whatsapp_phone_number"
                  className={`form-control ${
                    errors["communication.whatsapp_phone_number"]
                      ? "is-invalid"
                      : ""
                  }`}
                  placeholder="+1 (555) 123-4567"
                  value={formData.communication.whatsapp_phone_number}
                  onChange={handleInputChange}
                  disabled={loading || uploadingImage}
                />
                {errors["communication.whatsapp_phone_number"] && (
                  <div className="invalid-feedback">
                    {errors["communication.whatsapp_phone_number"]}
                  </div>
                )}
                <small className="text-muted">{t("form.whatsappHint")}</small>
              </div>

              {/* Instagram */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("form.instagramLabel")}
                </label>
                <div className="input-group">
                  <span className="input-group-text">@</span>
                  <input
                    type="text"
                    name="communication.instagram"
                    className="form-control"
                    placeholder="clubname"
                    value={formData.communication.instagram}
                    onChange={handleInputChange}
                    disabled={loading || uploadingImage}
                  />
                </div>
                <small className="text-muted">{t("form.instagramHint")}</small>
              </div>

              {/* Twitter */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  {t("form.twitterLabel")}
                </label>
                <div className="input-group">
                  <span className="input-group-text">@</span>
                  <input
                    type="text"
                    name="communication.twitter"
                    className="form-control"
                    placeholder="clubname"
                    value={formData.communication.twitter}
                    onChange={handleInputChange}
                    disabled={loading || uploadingImage}
                  />
                </div>
                <small className="text-muted">{t("form.twitterHint")}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={loading || uploadingImage}
        >
          <i className="fas fa-times me-2"></i>
          {tCommon("buttons.cancel")}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || uploadingImage}
        >
          {loading || uploadingImage ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              {uploadingImage
                ? t("form.uploading")
                : club
                  ? t("form.updating")
                  : t("form.creating")}
            </>
          ) : (
            <>
              <i className={`fas ${club ? "fa-save" : "fa-plus"} me-2`}></i>
              {club ? t("form.updateClub") : t("form.createClub")}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default ClubForm;
